import { useState, useEffect, useRef, Suspense, useCallback } from "react";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { MultiSelect } from "primereact/multiselect";
import { Tooltip } from "primereact/tooltip";
import AppLoading from "../app-loading/app-loading";
import {
  getCEDailyActivityTopcard,
  getCEDailyActivityStatusCards,
  CEDailyActivityTopcardRequest,
  CEDailyActivityTopcardResponse,
  CEDailyActivityStatusCard,
  getCustomerCallStatusSummary,
  CustomerCallStatusRequest,
  CustomerCallStatusResponseItem,
  getSalesExecutiveDailyReport,
  getLastFiveOrdersfromuserid,
  LastOrderResponse,
  OrderByOthersRequest,
  OrderByOthersItem 
} from "../../services/edit-users/edit-users.service";
import editUserServices from "../../services/edit-users/edit-users.service";
import OrdersPopup from "./OrderDetailsPopUp";
import { Button } from "primereact/button";

interface DeliveryExecutive {
  salesExecutiveId: number;
  salesExecutiveName: string;
}
interface SalesExecutiveDailyReport {
  reportDate: string;
  salesExecutiveID: number;
  salesExecutiveName: string;
  callCount: number;
  orderValue: number;
  invoiceValue: number;
}

interface CustomerCallStatusResponseItemWithDynamic extends CustomerCallStatusResponseItem {
  dynamicColumns?: Record<string, string>;
}

const STATUS_COLORS = {
  positive: { bg: "bg-green-50", border: "border-green-200", text: "text-green-800", number: "text-green-600", accent: "border-top-3 border-green-500" },
  neutral: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", number: "text-blue-600", accent: "border-top-3 border-blue-500" },
  negative: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-800", number: "text-orange-600", accent: "border-top-3 border-orange-500" },
  reject: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", number: "text-red-600", accent: "border-top-3 border-red-500" },
  other: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-800", number: "text-purple-600", accent: "border-top-3 border-purple-500" },
};

interface TopCardProps {
  title: string;
  value: string | number;
  color: string;
  tooltipId: string;
  tooltipContent: React.ReactNode;
  onClick?: () => void;
}

const TopCard = ({ title, value, color, tooltipId, tooltipContent, onClick }: TopCardProps) => (
  <div className="col-12 md:col-3 lg:col-3">
    <Card
      className={`shadow-md border-round-2xl text-center surface-50  border-${color}-500 relative cursor-pointer`}
      onClick={onClick}
    >
      {/* Info Icon */}
      <div className="absolute top-0" >
        <i
          className={`pi pi-info-circle text-${color}-500 cursor-pointer`}
          data-pr-tooltip-id={tooltipId}
        />
      </div>
      <h5 className={`text-${color}-700 text-base`}>{title}</h5>
      <p className={`text-xl font-semibold text-${color}-600 mt-1`}>{value ?? "--"}</p>
    </Card>


    <Tooltip target={`[data-pr-tooltip-id="${tooltipId}"]`} position="top">
      <div className="p-2 text-sm">{tooltipContent}</div>
    </Tooltip>
  </div>
);

const CEDailyActivityReport: React.FC = () => {
  const toast = useRef<Toast>(null);

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const [startDate, setStartDate] = useState<Date | null>(yesterday);
  const [endDate, setEndDate] = useState<Date | null>(today);

  const [loading, setLoading] = useState(false);
  const [topCardData, setTopCardData] = useState<CEDailyActivityTopcardResponse | null>(null);
  const [statusCards, setStatusCards] = useState<CEDailyActivityStatusCard[]>([]);
  const [executives, setExecutives] = useState<DeliveryExecutive[]>([]);
  const [selectedExecutive, setSelectedExecutive] = useState<DeliveryExecutive | null>(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const [customerData, setCustomerData] = useState<CustomerCallStatusResponseItemWithDynamic[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadingPopup, setLoadingPopup] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dynamicColumns, setDynamicColumns] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<{ callStatusId: number; callStatusName: string } | null>(null);
  const [sortField, setSortField] = useState<string>("customerName");
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(1);

  // Last Orders Popup state
  const [lastOrdersPopupVisible, setLastOrdersPopupVisible] = useState(false);
  const [lastOrdersData, setLastOrdersData] = useState<LastOrderResponse[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [loadingLastOrders, setLoadingLastOrders] = useState(false);

  // Orders By Me / Orders By Others / Total Orders popup state
  const [showOrdersByMe, setShowOrdersByMe] = useState(false);
  const [showOrdersByOthers, setShowOrdersByOthers] = useState(false);
  const [showTotalOrders, setShowTotalOrders] = useState(false);

  // Column selection - Fixed base columns definition (same as first component)
  const baseColumns = [
    { field: "customerName", header: "Customer Name" },
    { field: "contactNumber", header: "Contact Number" },
    { field: "orderID", header: "Last Ordered ID" },
    { field: "orderDate", header: " Last Ordered Date" },
    { field: "taxableAmount", header: "Last Ordered value" },
    { field: "lastCalledDate", header: "Last Called Date" },
  ];
  
  const [selectedColumns, setSelectedColumns] = useState<any[]>(baseColumns);
  const [allAvailableColumns, setAllAvailableColumns] = useState<any[]>(baseColumns);

  const [dailyCallsReport, setDailyCallsReport] = useState<SalesExecutiveDailyReport[]>([]);
  const [loadingDailyCalls, setLoadingDailyCalls] = useState(false);

 const formatDate = (date: Date) => date.toLocaleDateString("en-CA");
  const formatRupees = (amount: number) =>
    new Intl.NumberFormat("en-IN", { 
      style: "currency", 
      currency: "INR", 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount ?? 0));

  const formatNumber = (num: number) => Math.round(num ?? 0);

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "--";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return dateString;
    }
  };

  const formatTaxableAmount = (amount: string | null | undefined) => {
    if (!amount) return "--";
    try {
      const numAmount = Math.round(parseFloat(amount));
      return formatRupees(numAmount);
    } catch {
      return amount;
    }
  };

  const fetchExecutives = async () => {
    try {
      const execList = await editUserServices.getDeliveryExecutives();
      if (execList) setExecutives(execList);
    } catch (err) {
      console.error("Failed to fetch delivery executives", err);
    }
  };

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const requestPayload: CEDailyActivityTopcardRequest = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        salesExecutiveId: selectedExecutive?.salesExecutiveId ?? 0,
      };

      const [topCards, status, dailyCalls] = await Promise.all([
        getCEDailyActivityTopcard(requestPayload),
        getCEDailyActivityStatusCards(requestPayload),
        getSalesExecutiveDailyReport({
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
          salesExecutiveId: selectedExecutive?.salesExecutiveId ?? 0,
        }),
      ]);

      setTopCardData(topCards);
      setStatusCards(status);
      setDailyCallsReport(dailyCalls);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch daily activity data", life: 3000 });
    } finally {
      setLoading(false);
    }
  }, [selectedExecutive, startDate, endDate]);

  useEffect(() => {
    fetchExecutives();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const categorizeStatus = (statusName: string) => {
    const lowerStatus = statusName.toLowerCase();
    if (lowerStatus.includes("order received") || lowerStatus.includes("will order shortly")) return "positive";
    else if (lowerStatus.includes("don't call this number") || lowerStatus.includes("dnd") || lowerStatus.includes("do not disturb")) return "reject";
    else if (lowerStatus.includes("not interested") || lowerStatus.includes("refused") || lowerStatus.includes("rejected")) return "negative";
    else if (lowerStatus.includes("call back") || lowerStatus.includes("follow up") || lowerStatus.includes("pending")) return "neutral";
    else return "other";
  };

  const orderedStatusCards = [...statusCards].sort((a, b) => {
    const aCategory = categorizeStatus(a.callStatusName);
    const bCategory = categorizeStatus(b.callStatusName);
    const categoryOrder = { positive: 1, neutral: 2, other: 3, negative: 4, reject: 5 };
    return categoryOrder[aCategory] - categoryOrder[bCategory];
  });

  const columnBodyTemplate = (rowData: any, column: any) => {
    const value = rowData[column.field];

    if (column.field === "customerName") {
      return (
        <span
          className="text-blue-600 cursor-pointer underline"
          onClick={() => fetchLastFiveOrdersByUser(rowData.customerId)}
          title="Click to view last 5 orders"
        >
          {value || "--"}
        </span>
      );
    }

    switch (column.field) {
      case "orderDate":
        return formatDateTime(value);
      case "taxableAmount":
        return formatTaxableAmount(value);
      case "lastCalledDate":
        return formatDateTime(value);
      default:
        if (dynamicColumns.includes(column.field)) {
          return statusBodyTemplate(rowData.dynamicColumns?.[column.field] || "--");
        }
        return value || "--";
    }
  };

  const fetchLastFiveOrdersByUser = async (userId: number) => {
    if (!userId) return;

    setLoadingLastOrders(true);
    setSelectedCustomerId(userId);

    try {
      const orders = await getLastFiveOrdersfromuserid(userId);
      setLastOrdersData(orders);
      setLastOrdersPopupVisible(true);
    } catch (err) {
      console.error("Failed to fetch last 5 orders", err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch last 5 orders",
        life: 3000,
      });
    } finally {
      setLoadingLastOrders(false);
    }
  };

  const getStatusSeverity = (status: string) => {
    if (!status) return null;
    const statusLower = status.toLowerCase();
    if (statusLower.includes("order received")) return "success";
    if (statusLower.includes("call not received")) return "warning";
    if (statusLower.includes("not required")) return "info";
    if (statusLower.includes("will order shortly")) return "success";
    if (statusLower.includes("phone not reachable")) return "danger";
    if (statusLower.includes("call me later")) return "warning";
    return null;
  };

  const statusBodyTemplate = (value: string) => {
    const severity = getStatusSeverity(value);
    return severity ? <Tag value={value} severity={severity} /> : value;
  };

  const fetchCustomerCallStatus = async (
    callStatusId: number,
    callStatusName: string,
    page: number = pageNumber,
    size: number = pageSize,
    sortFieldParam: string = sortField,
    sortOrderParam: 1 | -1 | 0 = sortOrder
  ) => {
    if (!startDate || !endDate) return;

    setSelectedStatus({ callStatusId, callStatusName });
    setLoadingPopup(true);

    const requestData: CustomerCallStatusRequest = {
      salesExecutiveId: selectedExecutive?.salesExecutiveId,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      callStatusId,
      pageNumber: page,
      pageSize: size,
      sortColumn: sortFieldParam,
      sortDirection: sortOrderParam === 1 ? "asc" : "desc",
    };

    try {
      const response = await getCustomerCallStatusSummary(requestData);
      setCustomerData(response.data);
      setTotalRecords(response.totalCount);

      const allDynamicKeys: string[] = [];
      response.data.forEach((item: any) => {
        if (item.dynamicColumns) {
          Object.keys(item.dynamicColumns).forEach((key) => {
            if (!allDynamicKeys.includes(key)) {
              allDynamicKeys.push(key);
            }
          });
        }
      });

      const sortedDynamicKeys = allDynamicKeys.sort((a, b) => {
        const getDateFromKey = (key: string) => {
          const parts = key.split('_')[0].split('-');
          const monthName = parts[0];
          const year = parseInt(parts[1]);
          const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          const monthIndex = months.findIndex(m => m === monthName);
          return new Date(year, monthIndex);
        };
        return getDateFromKey(b).getTime() - getDateFromKey(a).getTime();
      });

      setDynamicColumns(sortedDynamicKeys);

      // Create dynamic column objects
      const dynamicColumnObjects = sortedDynamicKeys.map(field => ({
        field,
        header: field,
        isDynamic: true
      }));

      // Update all available columns
      const allColumns = [...baseColumns, ...dynamicColumnObjects];
      setAllAvailableColumns(allColumns);
      
      // Set selected columns to all columns by default
      setSelectedColumns(allColumns);

      setPopupVisible(true);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch customer details", life: 3000 });
    } finally {
      setLoadingPopup(false);
    }
  };

  const paginatorTemplate = {
    layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink",
    RowsPerPageDropdown: (options: any) => {
      const dropdownOptions = [
        { label: 10, value: 10 },
        { label: 20, value: 20 },
        { label: 50, value: 50 },
        { label: 100, value: 100 },
      ];
      return (
        <div className="flex align-items-center">
          <span className="text-sm text-600 mr-2">Show</span>
          <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} className="w-4rem" />
          <span className="text-sm text-600 ml-2">entries</span>
        </div>
      );
    },
    CurrentPageReport: (options: any) => (
      <span className="mx-3 text-sm text-600">
        Showing {options.first} to {options.last} of {totalRecords} entries
      </span>
    ),
  };

  const isOrderReceivedStatus = selectedStatus?.callStatusName.toLowerCase().includes("order received");

  return (
    <Suspense fallback={<AppLoading />}>
      <Toast ref={toast} />
      <div className="layout-content mt-3 mb-3">

        {/* ===== HEADER FILTERS ===== */}
        <div className="flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
          <h4 className="ml-1 mb-0 font-semibold text-gray-800">Customer Executive Daily Activity Report</h4>
          <div className="flex gap-2">
            <Dropdown
              value={selectedExecutive}
              options={executives}
              onChange={(e) => setSelectedExecutive(e.value)}
              optionLabel="salesExecutiveName"
              placeholder="Select Executive"
              className="w-48"
            />
            <Calendar value={startDate} onChange={(e) => setStartDate(e.value as Date)} placeholder="Start Date" showIcon dateFormat="dd/mm/yy"   readOnlyInput/>
            <Calendar value={endDate} onChange={(e) => setEndDate(e.value as Date)} placeholder="End Date" showIcon dateFormat="dd/mm/yy"   readOnlyInput />
          </div>
        </div>

        {/* ===== TOP CARDS ===== */}
        <div className="grid mb-3">
          {/* Reached Customers */}
          <TopCard
            title="Reached Customers"
            value={topCardData ? topCardData.totalCustomersCalledWithinRange : "--"}
            color="blue"
            tooltipId="reached-info"
            tooltipContent={
              <>
                <p className="mb-1 font-semibold">ℹ️ Reached Customers</p>
                <ul className="list-disc pl-4 text-left">
                  <li>Total number of customers contacted within the selected date range.</li>
                  <li>Indicates the number of customers reached by selected executive.</li>
                </ul>
              </>
            }
          />

          {/* Ordered By Me */}
          <TopCard
            title="Ordered By Me"
            value={topCardData ? formatRupees(topCardData.totalTaxableAmountFromReachedCustomers) : "--"}
            color="teal"
            tooltipId="ordered-me-info"
            tooltipContent={
              <>
                <p className="mb-1 font-semibold">ℹ️ Ordered By Me</p>
                <ul className="list-disc pl-4 text-left">
                  <li>Orders placed directly by a customer executive.</li>
                  <li>Status marked as "Order Received" in the CRM by the executive 
                    and the status is updated within the selected Date Range</li>
                  <li>Click the card to see detailed order information</li>
                </ul>
              </>
            }
           onClick={() => setShowOrdersByMe(true)}
          />

          {/* Ordered By Others */}
          <TopCard
            title="Ordered By Others"
            value={topCardData ? formatRupees(topCardData.totalTaxableAmountByOthers) : "--"}
            color="orange"
            tooltipId="ordered-others-info"
            tooltipContent={
              <>
                <p className="mb-1 font-semibold">ℹ️ Ordered By Others</p>
                <ul className="list-disc pl-4 text-left">
                  <li>Orders placed by someone other than assigned customer executive.</li>
                  <li>Click the card to see detailed order information.</li>
                </ul>
              </>
            }
            onClick={() => setShowOrdersByOthers(true)}
          />

          {/* Total Order Value */}
          <TopCard
            title="Total Order Value"
            value={topCardData ? formatRupees(topCardData.totalTaxableAmount) : "--"}
            color="green"
            tooltipId="total-value-info"
            tooltipContent={
              <>
                <p className="mb-1 font-semibold">ℹ️ Total Order Value</p>
                <ul className="list-disc pl-4 text-left">
                  <li>Total order value from all customers</li>
                  <li>Includes both self and others' orders</li>
                  <li>.Click the card to see detailed order information.</li>
                </ul>
              </>
            }
             onClick={() => setShowTotalOrders(true)}
          />
        </div>

        {/* ===== STATUS CARDS ===== */}
        <div className="grid">
          {orderedStatusCards.map((card, idx) => {
            const category = categorizeStatus(card.callStatusName);
            const colors = STATUS_COLORS[category];
            return (
              <div key={idx} className="col-6 md:col-3 lg:col-2">
                <Card
                  className={`shadow-sm border-round-xl text-center p-2 ${colors.bg} ${colors.border} ${colors.accent} cursor-pointer`}
                  onClick={() => fetchCustomerCallStatus(card.callStatusId, card.callStatusName)}
                >
                  <h6 className={`font-medium ${colors.text} mb-1 text-sm`}>{card.callStatusName}</h6>
                  <p className={`text-xl font-semibold ${colors.number}`}>{card.customerCount}</p>
                </Card>
              </div>
            );
          })}
        </div>

        {/* Orders Popups */}
     <OrdersPopup visible={showOrdersByMe}
      onHide={() => setShowOrdersByMe(false)} 
      startDate={formatDate(startDate!)} 
      endDate={formatDate(endDate!)} 
      salesExecutiveId={selectedExecutive?.salesExecutiveId} 
      type="byMe" 
      toastRef={toast} 
      />

     <OrdersPopup visible={showOrdersByOthers}
      onHide={() => setShowOrdersByOthers(false)}
      startDate={formatDate(startDate!)}
      endDate={formatDate(endDate!)}
      salesExecutiveId={selectedExecutive?.salesExecutiveId} 
      type="byOthers" 
      toastRef={toast} 
      />

     <OrdersPopup visible={showTotalOrders}
      onHide={() => setShowTotalOrders(false)}
      startDate={formatDate(startDate!)} 
      endDate={formatDate(endDate!)}
      salesExecutiveId={selectedExecutive?.salesExecutiveId}
      type="total" 
      toastRef={toast}        
      />
        {/* ===== CUSTOMER POPUP WITH COLUMN SELECTOR ===== */}
        <Dialog
          header={
            <div className="flex justify-content-between align-items-center w-full">
              <span>Customers - {selectedStatus?.callStatusName || ""}</span>
              <MultiSelect
                value={selectedColumns}
                options={allAvailableColumns}
                optionLabel="header"
                onChange={(e) => setSelectedColumns(e.value)}
                display="chip"
                placeholder="Select Columns"
                className="w-20rem"
                maxSelectedLabels={3}
                selectedItemsLabel="{0} columns selected"
              />
            </div>
          }
          visible={popupVisible}
          style={{ width: "90vw", maxWidth: "1200px" }}
          onHide={() => setPopupVisible(false)}
          modal
          className="customer-dialog"
        >
          {isOrderReceivedStatus && (
            <div className="mb-3 p-3 border-round-md bg-blue-50 text-blue-900 border-1 border-blue-200">
              <i className="pi pi-info-circle mr-2"></i>
              The last order from the customer was marked as received by the executive.
            </div>
          )}

          <DataTable
            value={customerData}
            paginator
            rows={pageSize}
            totalRecords={totalRecords}
            lazy
            loading={loadingPopup}
            first={(pageNumber - 1) * pageSize}
            onPage={(e) => {
              const newPage = (e.page ?? 0) + 1;
              const newSize = e.rows ?? pageSize;
              setPageNumber(newPage);
              setPageSize(newSize);
              if (selectedStatus) {
                fetchCustomerCallStatus(
                  selectedStatus.callStatusId, 
                  selectedStatus.callStatusName, 
                  newPage, 
                  newSize, 
                  sortField, 
                  sortOrder
                );
              }
            }}
            onSort={(e) => {
              setSortField(e.sortField || "customerName");
              setSortOrder((e.sortOrder as 1 | -1 | 0) ?? 0);
              if (selectedStatus) {
                fetchCustomerCallStatus(
                  selectedStatus.callStatusId, 
                  selectedStatus.callStatusName, 
                  pageNumber, 
                  pageSize, 
                  e.sortField || "customerName", 
                  (e.sortOrder as 1 | -1 | 0) ?? 0
                );
              }
            }}
            sortField={sortField}
            sortOrder={sortOrder}
            responsiveLayout="scroll"
            paginatorTemplate={paginatorTemplate}
            className="p-datatable-sm striped-rows"
            rowClassName={() => "cursor-pointer"}
            emptyMessage="No customers found."
            showGridlines
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                body={(rowData) => columnBodyTemplate(rowData, col)}
                sortable
                style={col.field === "taxableAmount" ? { textAlign: "right" } : {}}
              />
            ))}
          </DataTable>
        </Dialog>

        {/* ===== LAST ORDERS POPUP ===== */}
        <Dialog
          header="Last 5 Orders"
          visible={lastOrdersPopupVisible}
          style={{ width: "60vw", maxWidth: "700px", borderRadius: "8px" }}
          onHide={() => setLastOrdersPopupVisible(false)}
          modal
          className="last-orders-dialog"
          contentStyle={{ padding: "1rem", backgroundColor: "#f9f9f9" }}
          headerStyle={{ backgroundColor: "#007f3d", color: "#fff", fontWeight: 600, fontSize: "1.1rem", borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
        >
          <DataTable
            value={lastOrdersData}
            loading={loadingLastOrders}
            paginator
            rows={5}
            responsiveLayout="scroll"
            emptyMessage="No orders found."
            className="p-datatable-sm p-datatable-striped"
            showGridlines
            style={{ borderRadius: "6px", overflow: "hidden" }}
          >
            <Column field="orderID" header="Order ID" sortable style={{ width: "20%" }} />
            <Column field="createdDate" header="Order Date" body={(row) => formatDateTime(row.createdDate)} sortable style={{ width: "30%" }} />
            <Column field="taxableAmount" header="Taxable Amount" body={(row) => formatRupees(row.taxableAmount)} style={{ textAlign: "right", width: "25%" }} sortable />
            <Column field="createdByName" header="Created By" sortable style={{ width: "25%" }} />
          </DataTable>
        </Dialog>

        {/* ===== DAILY CALLS REPORT ===== */}
        <div className="mt-4">
          {/* <h5 className="ml-1 mb-2 font-semibold text-gray-800">Daily Calls Report</h5>
          <DataTable
            value={dailyCallsReport.filter((row) => row.callCount > 0)}
            loading={loadingDailyCalls}
            responsiveLayout="scroll"
            className="p-datatable-sm striped-rows"
            showGridlines
            emptyMessage="No daily calls found for the selected date range."
          >
            <Column field="reportDate" header="Date" body={(data) => formatDateTime(data.reportDate)} sortable />
            <Column field="callCount" header="Total Calls" sortable />
            <Column field="orderValue" header="Order Value" body={(data) => formatRupees(data.orderValue)} sortable />
          </DataTable>  */}
        </div>
      </div>
    </Suspense>
  );
};

export default CEDailyActivityReport;